import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { useWidth } from '../../lib/useWidth';
import {
  Button,
  Title,
  TablesCard,
  Spinner,
  PlusIcon,
  FarmIcon,
  Input,
  Dropdown,
} from '../../components/shared';
import GrowerImportSelectModal from '../../components/farm-modals/GrowerImportSelectModal';
import { checkRolePermission } from '../../entities/util-functions';
import { sendSingleRequest } from '../../apis';
import FarmsTable from '../../components/shared/tables/FarmsTable';
import { selectAccount, selectProfile } from '../../store/auth/auth.selector';
import { selectLang } from '../../store/ui/ui.selector';
import { IFarmCard } from '../../entities/farms.entities';
import FarmsMobile from '../../components/shared/table-mobile/FarmsMobile';
import { translate } from '../../lib/lang.helper';
import { selectAllFarms } from '../../store/extra/extra.selector';
import { toMillisecond } from '../../lib/common.helpers';
import moment from 'moment';
import { updateAccountSetting } from '../../store/auth/auth.actions';
import TaxonomyGroupFilter from '../../components/farm-util/TaxonomyGroupFilter';
import { StageLabelSpan } from '../../components/CommonViews';

const getSortOptions = (lang: any) => [
  {
    id: 'name_asc',
    value: 'name_asc',
    label: translate(lang, 'Farm Name (A-Z)'),
  },
  {
    id: 'name_desc',
    value: 'name_desc',
    label: translate(lang, 'Farm Name (Z-A)'),
  },
  {
    id: 'number_asc',
    value: 'number_asc',
    label: translate(lang, 'Farm Number (Low to High)'),
  },
  {
    id: 'number_desc',
    value: 'number_desc',
    label: translate(lang, 'Farm Number (High to Low)'),
  },
  {
    id: 'growingArea_asc',
    value: 'growingArea_asc',
    label: translate(lang, 'Growing Area (A-Z)'),
  },
  {
    id: 'growingArea_desc',
    value: 'growingArea_desc',
    label: translate(lang, 'Growing Area (Z-A)'),
  },
  {
    id: 'area_asc',
    value: 'area_asc',
    label: translate(lang, 'Total Area (Low to High)'),
  },
  {
    id: 'area_desc',
    value: 'area_desc',
    label: translate(lang, 'Total Area (High to Low)'),
  },
];

const Farms = () => {
  const width = useWidth();
  const history = useHistory();
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const allFarms = useSelector(selectAllFarms).filter(x => x.is_active);
  const profile = useSelector(selectProfile);
  const isEditable = profile?.edit_permission;
  const settings = useSelector(selectAccount);

  const [total, setTotal] = useState<IFarmCard[]>([]);
  const [isCardsSpinner, setIsCardsSpinner] = useState(false);
  const [visibleImport, setVisibleImport] = useState(false);

  const getTotals = useCallback(async () => {
    const response = await sendSingleRequest(
      {},
      'GET',
      'api/overview/farm-review',
      true,
    );
    if (response.status) {
      setTotal(response.data);
    }
  }, []);

  const { nextSeedData, nextHarvestData } = useMemo(() => {
    let nextSeedData: IFarmCard[] = [];
    let nextHarvestData: IFarmCard[] = [];
    for (const farm of allFarms) {
      for (const line of farm.lines) {
        if (
          line.growing_cycle &&
          toMillisecond(line.growing_cycle.main_seed.planned_date_seed) >=
            Date.now()
        ) {
          nextSeedData.push({
            name: `${farm.name} - ${line.line_name}`,
            date: line.growing_cycle.main_seed.planned_date_seed,
          });
        }
        if (line.growing_cycle && line.growing_cycle.ready_harvest) {
          const rd = toMillisecond(line.growing_cycle.ready_harvest);
          nextHarvestData.push({
            name: `${farm.name} - ${line.line_name}`,
            date: rd,
            unit: translate(
              lang,
              'Ready for %s days',
              moment().diff(rd, 'days'),
            ),
          });
        }
      }
    }
    nextSeedData.sort((a, b) => (a.date ?? 0) - (b.date ?? 0));
    nextSeedData = nextSeedData.slice(0, 3);
    nextHarvestData.sort((a, b) => (a.date ?? 0) - (b.date ?? 0));
    nextHarvestData = nextHarvestData.slice(0, 3);

    return { nextSeedData, nextHarvestData };
  }, [allFarms]);

  const loadData = useCallback(async () => {
    setIsCardsSpinner(true);
    await getTotals();
    setIsCardsSpinner(false);
  }, []);

  const [searchKey, setSearchKey] = useState('');
  const [sortKey, setSortKey] = useState(settings?.farms_sort_key ?? null);
  const [fltTaxonomyIds, setFltTaxonomyIds] = useState<number[]>([]);
  const [fltStage, setFltStage] = useState<number>();

  const changeSortKey = (key: string | null) => {
    dispatch(updateAccountSetting({ farms_sort_key: key }));
    setSortKey(key);
  };
  const filteredFarms = useMemo(() => {
    let result = allFarms.filter(
      x =>
        x.farm_number.toLowerCase().includes(searchKey.toLowerCase()) ||
        x.name.toLowerCase().includes(searchKey.toLowerCase()),
    );
    if (sortKey) {
      const [field, direction] = sortKey.split('_');
      result = result.sort((a, b) => {
        if (field === 'name') {
          return direction === 'asc'
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name);
        } else if (field === 'number') {
          if (isNaN(Number(a.farm_number)) || isNaN(Number(b.farm_number))) {
            return direction === 'asc'
              ? a.farm_number.localeCompare(b.farm_number)
              : b.farm_number.localeCompare(a.farm_number);
          }
          return direction === 'asc'
            ? Number(a.farm_number) - Number(b.farm_number)
            : Number(b.farm_number) - Number(a.farm_number);
        } else if (field === 'growingArea') {
          return direction === 'asc'
            ? (a.growing_area?.localeCompare(b.growing_area || '') ?? 0)
            : (b.growing_area?.localeCompare(a.growing_area || '') ?? 0);
        } else if (field === 'area') {
          return direction === 'asc'
            ? (a.area || 0) - (b.area || 0)
            : (b.area || 0) - (a.area || 0);
        } else {
          return a.id - b.id;
        }
      });
    }
    if (fltTaxonomyIds.length > 0) {
      result = result.filter(farm =>
        farm.lines.some(line =>
          line.growing_cycle?.taxonomy_groups?.some(tg =>
            fltTaxonomyIds.includes(tg.id),
          ),
        ),
      );
    }
    if (fltStage) {
      result = result.map(x => ({
        ...x,
        lines: x.lines.filter(
          l => l.growing_cycle?.main_seed.stage === fltStage,
        ),
      }));
      result = result.filter(farm => farm.lines.length > 0);
    }
    return result;
  }, [allFarms, searchKey, sortKey, fltTaxonomyIds, fltStage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className='h-calc-80 bg-secondary'>
      <div className='container pos-relative'>
        <div className='farms'>
          <div className='farms__header d-flex justify-between align-center'>
            <div>
              <Title size={5} color='black-3' align='default' fontWeight={700}>
                {translate(lang, 'Farms')}
              </Title>
              {checkRolePermission(
                { deniedRoles: ['processing_plant'] },
                profile?.role,
              ) && (
                <div>
                  <Button
                    size={width > 768 ? 3 : 5}
                    type='fill'
                    width='small'
                    color='blue'
                    onClick={() => history.push('/lines')}
                  >
                    {translate(lang, 'All lines')}
                  </Button>
                  <Button
                    size={width > 768 ? 3 : 5}
                    type='fill'
                    width='small'
                    color='blue'
                    className={width > 768 ? 'ml-7' : 'mt-7'}
                    onClick={() => history.push('/farms/visual-mode')}
                  >
                    {translate(lang, 'Farms map')}
                  </Button>

                  <Button
                    size={width > 768 ? 3 : 5}
                    type='fill'
                    width='small'
                    color='blue'
                    className={width > 768 ? 'ml-7' : 'mt-7'}
                    onClick={() => history.push('/spats')}
                  >
                    {translate(lang, 'Farm spats')}
                  </Button>
                  {profile?.account_type === 'grower' ? (
                    <Button
                      size={width > 768 ? 3 : 5}
                      type='bordered'
                      width='small'
                      color='green'
                      className={width > 768 ? 'ml-7' : 'mt-7'}
                      onClick={() => setVisibleImport(true)}
                    >
                      {translate(lang, 'Import')}
                    </Button>
                  ) : (
                    <Button
                      size={width > 768 ? 3 : 5}
                      type='fill'
                      width='small'
                      color='blue'
                      className={width > 768 ? 'ml-7' : 'mt-7'}
                      onClick={() => history.push('/line-spats')}
                    >
                      {translate(lang, 'Spat count')}
                    </Button>
                  )}
                </div>
              )}
            </div>
            {!!isEditable && (
              <div className='d-flex justify-content-right align-center'>
                <Link to='/farms/farm-add'>
                  {width > 768 ? (
                    <Button color='blue' size={1} width='middle' type='fill'>
                      {translate(lang, 'Add Farm')}
                    </Button>
                  ) : (
                    <Button
                      color='blue'
                      size={0}
                      width='default'
                      type='fill'
                      iconOnly
                    >
                      <PlusIcon />
                    </Button>
                  )}
                </Link>
                {profile.account_type === 'operator' && (
                  <Link className='ml-10' to='/line-group'>
                    {width > 768 ? (
                      <Button color='green' size={1} width='middle' type='fill'>
                        {translate(lang, 'Line Group')}
                      </Button>
                    ) : (
                      <Button
                        color='blue'
                        size={0}
                        width='default'
                        type='fill'
                        iconOnly
                      >
                        <FarmIcon />
                      </Button>
                    )}
                  </Link>
                )}
              </div>
            )}
          </div>
          <div className='farms__content d-flex justify-between'>
            <div className='w-100 pos-relative overflow-auto'>
              <div className='d-flex justify-between align-end mb-16'>
                <div className='d-flex align-end gap-12'>
                  <div style={{ maxWidth: '350px' }}>
                    <Input
                      type='text'
                      label=''
                      placeholder={translate(lang, 'Search farms')}
                      value={searchKey}
                      onChange={e => setSearchKey(e.target.value)}
                    />
                  </div>
                  <div style={{ width: '200px' }}>
                    <Dropdown
                      label=''
                      placeholder={translate(lang, 'Batch Stage')}
                      options={[0, 1, 2, 3, 4].map(x => ({
                        id: x.toString(),
                        value: x.toString(),
                        label:
                          x === 0 ? (
                            translate(lang, 'All')
                          ) : (
                            <StageLabelSpan stage={x} />
                          ),
                      }))}
                      value={fltStage?.toString()}
                      onChange={v =>
                        setFltStage(Number(v) ? Number(v) : undefined)
                      }
                    />
                  </div>
                  <TaxonomyGroupFilter
                    className='wp-320'
                    value={fltTaxonomyIds}
                    onChange={setFltTaxonomyIds}
                    hideLabel={true}
                  />
                </div>
                <Dropdown
                  className='wp-280'
                  placeholder={translate(lang, 'Sort By')}
                  value={sortKey ?? undefined}
                  onChange={v => changeSortKey(v)}
                  allowClear
                  options={getSortOptions(lang)}
                />
              </div>
              {width > 768 ? (
                <FarmsTable farmsData={filteredFarms} isActive={true} />
              ) : (
                <FarmsMobile data={filteredFarms} />
              )}
            </div>
            <div className='farms__content-cards flex-basis-32'>
              {!isCardsSpinner ? (
                <>
                  <TablesCard data={nextSeedData} type='next-seed' />
                  <TablesCard data={nextHarvestData} type='next-harvest' />
                  {total?.length > 0 && <TablesCard data={total} />}
                </>
              ) : (
                <div className='mt-24'>
                  <Spinner />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {visibleImport && (
        <GrowerImportSelectModal
          visible={visibleImport}
          onClose={() => setVisibleImport(false)}
          onConfirm={() => {
            setVisibleImport(false);
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default Farms;
